import { useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { getGeoService } from 'services/geoService';

import type { LatLng } from '../@types';
import { AppContext } from '../context/app';
import type { Place, SatellitePlace } from '../context/types';
import { isLatLongString } from '../utils/typeGuards';
import { useKioskManager } from './useKioskManager';
import { useNetworkManager } from './useNetworkManager';

export function useLocationParams(): any {
  const geoService = useMemo(() => getGeoService(), []);

  const { search } = useLocation();
  const { addressSearchResult, setAddressSearchResult } =
    useContext(AppContext);
  const { placesData, satellitePlacesData } = useNetworkManager();
  const { kioskRegistration } = useKioskManager();
  const [needFetching, setNeedFetching] = useState(false);

  const params = useMemo(() => new URLSearchParams(search), [search]);
  const destinationId = params.get('destinationid');
  const startingId = params.get('startingid');
  const startingLat = parseFloat(params.get('startingLat') || '');
  const startingLon = parseFloat(params.get('startingLon') || '');
  const startingAddress = params.get('startingAddress') || '';

  useEffect(() => {
    if (addressSearchResult?.address === startingAddress) return;
    setNeedFetching(true);
  }, [addressSearchResult, startingAddress]);

  useEffect(() => {
    if (addressSearchResult?.address === startingAddress) return;
    if (!needFetching) return;
    const reverseSearchAddress = async () => {
      if (startingLat && startingLon && !startingAddress) {
        const results = await geoService.reverseGeocode([
          startingLat,
          startingLon,
        ]);
        if (results) {
          if (addressSearchResult?.address !== results[0].address) {
            setAddressSearchResult(results[0]);
          }
        }
      } else if (startingAddress) {
        const result = await geoService.geocode(startingAddress);
        if (result && addressSearchResult?.address !== result.address) {
          setAddressSearchResult(result);
        }
      }
      setNeedFetching(false);
    };
    reverseSearchAddress();
  }, [
    geoService,
    needFetching,
    addressSearchResult,
    setAddressSearchResult,
    startingAddress,
    startingLat,
    startingLon,
  ]);

  const stringToLatLong = (str: string): LatLng => {
    const coords = str.split(',');
    return { lat: +coords[0], lng: +coords[1] };
  };

  const navPlaces = useMemo<any>(() => {
    const navigationPlaces: any = {
      navStartingPlace: undefined,
      navDestinationPlace: undefined,
    };

    if (destinationId) {
      navigationPlaces.navDestinationPlace =
        placesData?.find((location: Place) => location.id === +destinationId) ||
        satellitePlacesData?.find(
          (location: SatellitePlace) =>
            destinationId && location.id === +destinationId,
        );
    }

    // Not passing in a starting ID when in kiosk mode.
    // There should be kiosk data to use as the starting location
    if (kioskRegistration) {
      navigationPlaces.navStartingPlace = kioskRegistration;
    } else if (
      !startingId &&
      !startingLat &&
      !startingLon &&
      !startingAddress
    ) {
      navigationPlaces.navStartingPlace = undefined;
      return navigationPlaces;
    }

    if (isLatLongString(startingId)) {
      navigationPlaces.navStartingPlace = stringToLatLong(startingId || '');
      return navigationPlaces;
    }

    const campusPlace = placesData?.find(
      (location: Place) => startingId && location.id === +startingId,
    );

    const satellitePlace = satellitePlacesData?.find(
      (location: SatellitePlace) => startingId && location.id === +startingId,
    );

    if (campusPlace) {
      navigationPlaces.navStartingPlace = campusPlace;
      return navigationPlaces;
    }

    if (satellitePlace) {
      navigationPlaces.navStartingPlace = satellitePlace;
      return navigationPlaces;
    }

    if (addressSearchResult) {
      navigationPlaces.navStartingPlace = addressSearchResult;
      return navigationPlaces;
    }

    return navigationPlaces;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    destinationId,
    kioskRegistration,
    placesData,
    satellitePlacesData,
    startingId,
    addressSearchResult,
  ]);

  return navPlaces;
}
